import React from 'react';

import { FormattedMessage } from '../../util/reactIntl';
import { isFieldForListingType } from '../../util/fieldHelpers';

import { Heading, InfoTooltip, NamedLink } from '../../components';

import css from './ListingPage.module.css';

// Sizing-guide tooltips for container/complete-system fit fields, keyed by listing field key.
const SIZING_TOOLTIP_CATEGORIES = ['rigs', 'complete-systems'];
const CONTAINER_SIZING_TOOLTIP = {
  titleId: 'ListingPage.containerSizingTooltipTitle',
  textId: 'ListingPage.containerSizingTooltipText',
  guidePageName: 'ContainerSizingGuidePage',
};
const HARNESS_SIZING_TOOLTIP = {
  titleId: 'ListingPage.harnessSizingTooltipTitle',
  textId: 'ListingPage.harnessSizingTooltipText',
  guidePageName: 'HarnessSizingGuidePage',
};
const SIZING_TOOLTIPS = {
  allowedRangeMin: CONTAINER_SIZING_TOOLTIP,
  allowedRangeMax: CONTAINER_SIZING_TOOLTIP,
  weight_rigs: HARNESS_SIZING_TOOLTIP,
  height_rigs: HARNESS_SIZING_TOOLTIP,
};

const sizingTooltipMaybe = (key, category, intl) => {
  const tooltip = SIZING_TOOLTIPS[key];
  if (!tooltip || !SIZING_TOOLTIP_CATEGORIES.includes(category)) {
    return null;
  }
  return (
    <InfoTooltip
      className={css.detailInfoTooltip}
      title={intl.formatMessage({ id: tooltip.titleId })}
      text={
        <FormattedMessage
          id={tooltip.textId}
          values={{
            guideLink: (
              <NamedLink name={tooltip.guidePageName}>
                <FormattedMessage id="ListingPage.sizingTooltipGuideLink" />
              </NamedLink>
            ),
          }}
        />
      }
    />
  );
};

const SectionDetailsMaybe = props => {
  const { publicData, metadata = {}, listingFieldConfigs, isFieldForCategory, intl } = props;

  if (!publicData || !listingFieldConfigs) {
    return null;
  }

  const pickListingFields = (filteredConfigs, config) => {
    const { key, schemaType, enumOptions, showConfig = {} } = config;
    const listingType = publicData.listingType;
    const isTargetListingType = isFieldForListingType(listingType, config);
    const isTargetCategory = isFieldForCategory(config);

    const { isDetail, label } = showConfig;
    const publicDataValue = publicData[key];
    const metadataValue = metadata[key];
    const value = typeof publicDataValue != null ? publicDataValue : metadataValue;

    if (isDetail && isTargetListingType && isTargetCategory && typeof value !== 'undefined') {
      const findSelectedOption = enumValue => enumOptions?.find(o => enumValue === `${o.option}`);
      const getBooleanMessage = value =>
        value
          ? intl.formatMessage({ id: 'SearchPage.detailYes' })
          : intl.formatMessage({ id: 'SearchPage.detailNo' });
      const optionConfig = findSelectedOption(value);

      return schemaType === 'enum'
        ? filteredConfigs.concat({ key, value: optionConfig?.label, label })
        : schemaType === 'boolean'
        ? filteredConfigs.concat({ key, value: getBooleanMessage(value), label })
        : schemaType === 'long'
        ? filteredConfigs.concat({ key, value, label })
        : filteredConfigs;
    }
    return filteredConfigs;
  };

  const existingListingFields = listingFieldConfigs.reduce(pickListingFields, []);

  return existingListingFields.length > 0 ? (
    <section className={css.sectionDetails}>
      <Heading as="h2" rootClassName={css.sectionHeading}>
        <FormattedMessage id="ListingPage.detailsTitle" />
      </Heading>
      <ul className={css.details}>
        {existingListingFields.map(detail => (
          <li key={detail.key} className={css.detailsRow}>
            <span className={css.detailLabel}>
              {detail.label}
              {sizingTooltipMaybe(detail.key, publicData.categoryLevel1, intl)}
            </span>
            <span>{detail.value}</span>
          </li>
        ))}
      </ul>
    </section>
  ) : null;
};

export default SectionDetailsMaybe;
